import { type ReactElement, useState } from 'react';
import { useDashboardSummaryQuery } from '@/entities/dashboard';
import {
  type DashboardPeriodSelection,
  PeriodTabs,
  presetSelection,
  toSummaryInput,
} from '@/features/dashboard/period';
import { Button, PageHeader } from '@/shared/ui';
import { DashboardPendingQuotesWidget } from '@/widgets/dashboard-pending-quotes';
import { DashboardPopularProductsWidget } from '@/widgets/dashboard-popular-products';
import { DashboardShortcutsWidget } from '@/widgets/dashboard-shortcuts';
import { DashboardSummaryCardsWidget } from '@/widgets/dashboard-summary-cards';

/**
 * 어드민 대시보드(docs/superpowers/specs/2026-10-26-admin-dashboard-notes.md) — 기본 진입 화면.
 * 기간 선택만 소유하고 요약 하나를 읽어 위젯에 나눠 준다. 숫자 카드의 발행 견적·신규 가입과 인기 상품에만 기간이 걸린다.
 * 셸(헤더·사이드바·본문 여백)은 레이아웃 라우트가 준다.
 */
export function DashboardPage(): ReactElement {
  // 화면이 열린 시각 — 프리셋 계산과 경과 표시의 기준. 새로고침 전까지 고정한다.
  const [now] = useState(() => new Date());
  const [selection, setSelection] = useState<DashboardPeriodSelection>(() =>
    presetSelection('7d', now),
  );
  const summary = useDashboardSummaryQuery(toSummaryInput(selection));

  return (
    <>
      <PageHeader
        title="대시보드"
        description="오늘 처리할 상담과 전체 현황을 한눈에 봅니다."
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            isLoading={summary.isFetching}
            onClick={() => void summary.refetch()}
          >
            새로고침
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        <PeriodTabs value={selection} onChange={setSelection} now={now} />

        {summary.isError ? (
          <p
            role="alert"
            className="rounded-2xl border border-destructive/30 bg-destructive/5 px-5 py-4 text-sm text-destructive"
          >
            대시보드를 불러오지 못했습니다: {summary.error.message}
          </p>
        ) : null}

        {summary.data === undefined ? (
          summary.isPending ? (
            <p role="status" className="py-10 text-sm text-muted-foreground">
              대시보드를 불러오는 중입니다.
            </p>
          ) : null
        ) : (
          <>
            <DashboardSummaryCardsWidget now={summary.data.now} period={summary.data.periodStats} />
            <DashboardPendingQuotesWidget items={summary.data.pendingQuotes} now={now} />
            <div className="grid gap-6 xl:grid-cols-[3fr_2fr]">
              <DashboardPopularProductsWidget items={summary.data.popularProducts} />
              <DashboardShortcutsWidget />
            </div>
          </>
        )}
      </div>
    </>
  );
}
